// src/pages/info/DBCollectors/CollectorsDetails.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Split from 'react-split';
import '../../CheckPage/CheckPage.css';
import '../../../components/Modals/ModalBase.css';
import '../DBHouse/HouseDetails.css';
import './CollectorsDetails.css';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';
import Select from 'react-select';
import Modal from 'react-modal';
import API_BASE_URL from '../../../config';

Modal.setAppElement('#root');

const orDash = value => {
  if (value === null || value === undefined) return '-';
  const str = String(value).trim();
  return str === '' || str === '0' ? '-' : str;
};

const CollectorsDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();                      // collector_id из URL
  const location = useLocation();

  // Откуда пришли: 'map' или 'db'
  // Откуда пришли: 'map', 'db' или 'summary'
  const initialSource = location.state?.source || 'db';
  const [source] = useState(initialSource);
  // Заголовок в \"хлебных крошках\" — в зависимости от source
  let sourceLabel;
 if (source === 'priority') {
   sourceLabel = 'Карта приоритетов';
 } else if (source === 'map') {
   sourceLabel = 'Карта коллекторов';
 } else if (source === 'summary') {
   sourceLabel = 'Сводный анализ';
 } else {
   sourceLabel = 'БД коллекторов';
 }

  // Состояния
  const [collector, setCollector] = useState(null);
  const [loadingCollector, setLoadingCollector] = useState(true);
  const [designData, setDesignData] = useState(null);
  const [selectedImage, setSelectedImage] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [splitSizes, setSplitSizes] = useState([28, 72]);

  // Новое состояние для проверки наличия записей в журнале поломок
  const [hasBreaks, setHasBreaks] = useState(false);
  const [loadingBreaks, setLoadingBreaks] = useState(true);

  // ────────────────────────────────────────────────────────────────────────────────
  // 1) Подгружаем collector по ID (сначала из state, затем обязательно из API)
  useEffect(() => {
    const stateCollector = location.state?.collectorData || null;
    if (stateCollector && Number(stateCollector.id) === Number(id)) {
      setCollector(stateCollector);
      setLoadingCollector(false);
    }

    fetch(`${API_BASE_URL}/collector/get_collector_by_id/${id}`)
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(data => {
        setCollector(data);
      })
      .catch(err =>
        console.error('[CollectorsDetails] Ошибка загрузки коллектора по ID:', err)
      )
      .finally(() => setLoadingCollector(false));
  }, [id, location.state]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 2) Когда collector получен, подгружаем список конструктивных решений (design)
  useEffect(() => {
    if (!collector?.collector_design_id) return;

    fetch(`${API_BASE_URL}/collector/get_all_design`)
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(list => {
        const found = list.find(d => d.id === collector.collector_design_id);
        if (!found) return;
        setDesignData(found);
        if (found.images?.length && !selectedImage) {
          setSelectedImage(found.images[0].url);
        }
      })
      .catch(err =>
        console.error(
          '[CollectorsDetails] Ошибка загрузки конструктивных решений:',
          err
        )
      );
  }, [collector, selectedImage]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 3) Проверяем, есть ли записи в журнале поломок для данного коллектора
  useEffect(() => {
    setLoadingBreaks(true);
    fetch(`${API_BASE_URL}/breake_problem/list_by_collector/${id}`)
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setHasBreaks(true);
        } else {
          setHasBreaks(false);
        }
      })
      .catch(err => {
        console.error('[CollectorsDetails] Ошибка при загрузке записей журнала поломок:', err);
        // В случае ошибки считаем, что записей нет, чтобы не блокировать возможность удаления
        setHasBreaks(false);
      })
      .finally(() => setLoadingBreaks(false));
  }, [id]);

  // ────────────────────────────────────────────────────────────────────────────────
  if (loadingCollector) {
    return <div className="house-details-page">Загрузка данных коллектора…</div>;
  }
  if (!collector) {
    return <div className="house-details-page">Коллектор не найден</div>;
  }

  // ────────────────────────────────────────────────────────────────────────────────
  const openLightbox = url => {
    setLightboxSrc(url);
    setLightboxOpen(true);
  };

  const {
    name,
    year,
    material,
    length,
    diameter,
    node_flow,
    expenditure,
    technical_condition,
  } = collector;

  // ────────────────────────────────────────────────────────────────────────────────
  // Обработчик подтверждения удаления
  const handleDeleteConfirm = async () => {
    console.log('[CollectorsDetails] Удаляем коллектор id =', id);

    try {
      const response = await fetch(`${API_BASE_URL}/collector/delete_collector/${id}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });

      console.log('[CollectorsDetails] Ответ сервера:', response);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[CollectorsDetails] Ошибка при удалении:', errorText);
      } else {
        const resultText = await response.text().catch(() => null);
        console.log('[CollectorsDetails] Успешно удалено, ответ:', resultText);

        // Навигация в зависимости от источника (map или db)
        const targetRoute = source === 'map' ? '/collectors' : '/db-collectors';
        navigate(targetRoute, { state: { deletedId: Number(id) } });
      }
    } catch (err) {
      console.error('[CollectorsDetails] NetworkError при удалении:', err);
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="house-details-page">
      <Split
        className="house-split"
        sizes={splitSizes}
        minSize={[450, 700]}
        maxSize={[900, Infinity]}
        gutterSize={6}
        direction="horizontal"
        cursor="col-resize"
        onDragEnd={setSplitSizes}
      >
        {/* ───────────── ЛЕВАЯ ПАНЕЛЬ ───────────────────────────────────────── */}
        <div className="house-panel-left">
          <div
            className="breadcrumbs-fixed"
            style={{ userSelect: 'none' }}
            onMouseEnter={() =>
              document.querySelector('.breadcrumb-link')?.classList.add('hover')
            }
            onMouseLeave={() =>
              document.querySelector('.breadcrumb-link')?.classList.remove('hover')
            }
          >
            <span
              className="breadcrumb-link clickable"
              onClick={() => {
              if (source === 'priority') {
                navigate('/pipeline');
              } else if (source === 'map') {
                navigate('/collectors');
              } else if (source === 'summary') {
                navigate('/summary-reports');
              } else {
                navigate('/db-collectors');
              }
              }}
            >
              {sourceLabel}
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Просмотр коллектора</span>
          </div>

          <h2 className="house-address">{orDash(name)}</h2>
          <p className="house-details-text">
            <strong>Конструктивное решение:</strong>{' '}
            {orDash(collector.design?.collector_design)}
          </p>
          <p className="house-details-text">
            <strong>Год ввода:</strong> {orDash(year)}
          </p>
          <p className="house-details-text">
            <strong>Материал:</strong> {orDash(material)}
          </p>
          <p className="house-details-text">
            <strong>Длина:</strong> {orDash(length)}
          </p>

          <div className="CD-text">
            <p>
              <strong>Диаметр</strong>
            </p>
            {typeof diameter === 'string'
              ? diameter.split('@@').map((seg, i) => (
                  <p key={i}>{orDash(seg)}</p>
                ))
              : Object.entries(diameter || {}).map(([key, val]) => (
                  <p key={key}>
                    {key}: {orDash(val)}
                  </p>
                ))}

            <p>
              <strong>Узловые расходы</strong>
            </p>
            {typeof node_flow === 'string' ? (
              node_flow.split('@@').map((seg, i) => (
                <p key={i}>{orDash(seg)}</p>
              ))
            ) : Array.isArray(node_flow) ? (
              node_flow.map((nf, i) => <p key={i}>{orDash(nf)}</p>)
            ) : (
              <p>{orDash(node_flow)}</p>
            )}

            <p>
              <strong>Расходы</strong>
            </p>
            <p>{orDash(expenditure)}</p>

            <p>
              <strong>Техническое состояние</strong>
            </p>
            {typeof technical_condition === 'string' ? (
              technical_condition.split('@@').map((seg, i) => (
                <p key={i}>{orDash(seg)}</p>
              ))
            ) : Array.isArray(technical_condition) ? (
              technical_condition.map((tc, i) => <p key={i}>{orDash(tc)}</p>)
            ) : (
              <p>{orDash(technical_condition)}</p>
            )}
          </div>
        </div>

        {/* ───────────── ПРАВАЯ ПАНЕЛЬ ───────────────────────────────────────── */}
        <div className="house-panel-right">
          <div className="house-toolbar">
            <div className="drawing-select-block">
              <Select
                className="modal-select"
                classNamePrefix="modal-select"
                options={
                  designData?.images?.map((img, i) => ({
                    value: img.url,
                    label: `Чертёж ${i + 1}`,
                  })) || []
                }
                value={
                  selectedImage
                    ? {
                        value: selectedImage,
                        label: `Чертёж ${
                          designData.images.findIndex(i => i.url === selectedImage) + 1
                        }`,
                      }
                    : null
                }
                onChange={opt => setSelectedImage(opt?.value || '')}
                placeholder="Выбор чертежа"
                menuPortalTarget={document.body}
                menuPosition="absolute"
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                  control: base => ({
                    ...base,
                    backgroundColor: '#15171D',
                    borderColor: '#3B3B40',
                    borderRadius: 8,
                    minHeight: 42,
                    padding: 2,
                  }),
                  singleValue: base => ({ ...base, color: '#e5e7eb' }),
                  placeholder: base => ({ ...base, color: '#8b919d' }),
                  menu: base => ({
                    ...base,
                    backgroundColor: '#2a2c33',
                    borderColor: '#3B3B40',
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? '#3B3B40' : '#2a2c33',
                    color: '#e5e7eb',
                  }),
                }}
              />
            </div>
            <div className="house-toolbar-buttons">
              {/* Кнопка "Удалить запись" появляется только если записей в журнале нет */}
              {!loadingBreaks && !hasBreaks && (
                <button
                  className="primary-btn delete"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Удалить запись
                </button>
              )}
              <button
                className="primary-btn nn"
                onClick={() =>
                  navigate('/edit-collector', {
                    state: { collectorData: collector, source },
                  })
                }
              >
                Редактировать запись
              </button>
            </div>
          </div>

          <div
            className="house-image-area"
            onClick={() => selectedImage && openLightbox(selectedImage)}
          >
            {selectedImage ? (
              <img
                src={selectedImage}
                alt="Выбранный чертёж"
                className="house-drawing"
              />
            ) : designData?.images?.length > 0 ? (
              <div className="gallery-container">
                {designData.images.map((img, i) => (
                  <img
                    key={i}
                    src={img.url}
                    alt={`Чертёж ${i + 1}`}
                    className="house-drawing"
                    onClick={() => setSelectedImage(img.url)}
                  />
                ))}
              </div>
            ) : (
              <div className="house-image-placeholder">(Чертежи не найдены)</div>
            )}
          </div>

          <Modal
            isOpen={lightboxOpen}
            onRequestClose={() => setLightboxOpen(false)}
            className="lightbox-modal"
            overlayClassName="lightbox-overlay"
          >
            <img
              src={lightboxSrc}
              alt="Чертёж во весь экран"
              className="lightbox-image"
              onClick={() => setLightboxOpen(false)}
            />
          </Modal>
        </div>
      </Split>

      {showDeleteModal && (
        <DeleteConfirmationModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default CollectorsDetails;
